import React, { createContext, useContext, useState, useCallback } from 'react';
import { useEducationAuth } from './EducationAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AcademyContext = createContext(null);

export const AcademyProvider = ({ children }) => {
  const { token } = useEducationAuth();
  const [courses, setCourses] = useState([]);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [progress, setProgress] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };

  // Fetch all courses
  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/academy/courses`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses || []);
        return { success: true, courses: data.courses };
      } else {
        return { success: false, error: 'Failed to fetch courses' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch single course
  const fetchCourse = useCallback(async (courseSlug) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/academy/courses/${courseSlug}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCurrentCourse(data);
        return { success: true, course: data };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Course not found' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch module
  const fetchModule = useCallback(async (courseSlug, moduleSlug) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/academy/courses/${courseSlug}/modules/${moduleSlug}`,
        { headers }
      );
      if (response.ok) {
        const data = await response.json();
        setCurrentModule(data);
        return { success: true, module: data };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Module not found' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch lesson
  const fetchLesson = useCallback(async (courseSlug, moduleSlug, lessonId) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/academy/courses/${courseSlug}/modules/${moduleSlug}/lessons/${lessonId}`,
        { headers }
      );
      if (response.ok) {
        const data = await response.json();
        setCurrentLesson(data);
        return { success: true, lesson: data };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Lesson not found' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Complete lesson
  const completeLesson = useCallback(async (courseSlug, lessonId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/academy/courses/${courseSlug}/lessons/${lessonId}/complete`,
        {
          method: 'POST',
          headers,
        }
      );
      if (response.ok) {
        const data = await response.json();
        // Update current lesson if it matches
        if (currentLesson?.id === lessonId) {
          setCurrentLesson(prev => ({ ...prev, is_completed: true }));
        }
        return { success: true, ...data };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }, [token, currentLesson]);

  // Fetch user progress
  const fetchProgress = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/academy/my-progress`, { headers });
      if (response.ok) {
        const data = await response.json();
        setProgress(data.progress || []);
        return { success: true, progress: data.progress };
      } else {
        return { success: false, error: 'Failed to fetch progress' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }, [token]);

  // Fetch certificates
  const fetchCertificates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/academy/certificates`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCertificates(data.certificates || []);
        return { success: true, certificates: data.certificates };
      } else {
        return { success: false, error: 'Failed to fetch certificates' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }, [token]);

  // Get course progress
  const getCourseProgress = (courseId) => {
    return progress.find(p => p.course_id === courseId) || null;
  };

  const value = {
    courses,
    currentCourse,
    currentModule,
    currentLesson,
    progress,
    certificates,
    loading,
    fetchCourses,
    fetchCourse,
    fetchModule,
    fetchLesson,
    completeLesson,
    fetchProgress,
    fetchCertificates,
    getCourseProgress,
  };

  return (
    <AcademyContext.Provider value={value}>
      {children}
    </AcademyContext.Provider>
  );
};

export const useAcademy = () => {
  const context = useContext(AcademyContext);
  if (!context) {
    throw new Error('useAcademy must be used within an AcademyProvider');
  }
  return context;
};

export default AcademyContext;
